import { FilterParams } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { MenuItem, Select } from '@mui/material';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { ReactElement } from 'react';
import { useFilter } from '@synergy/components';

import { useAppSelector } from '../../../store/use-appstate-selector';

const ALL = 'all';

/**
 * PALO quick filter toggling between all documents and the current user's own,
 * over the legacy filter context. Keeps the filter-map registration so the
 * responsible-contact chip still shows the contact name.
 */
export const PaloResponsibleQuickFilter = (): ReactElement => {
    const { filter, updateFilter, registerFilterMap } = useFilter();
    const { contact: userContact } = useAppSelector(state => state.user);
    const { flat: contacts } = useAppSelector(state => state.contacts);

    registerFilterMap(FilterParams.ResponsibleContactId, (id: unknown) => {
        const contactId = Number(id);
        if (!contactId) {
            return '';
        }
        if (contactId === userContact.id) {
            return userContact.fullName;
        }

        return contacts.find(contact => contact.id === contactId)?.fullName;
    });

    const current = filter.get(FilterParams.ResponsibleContactId);
    const value = current === String(userContact.id) ? String(userContact.id) : ALL;

    const onChange = (next: string): void => {
        filter.delete(FilterParams.ResponsibleContactId);
        if (next !== ALL) {
            filter.set(FilterParams.ResponsibleContactId, next);
        }
        updateFilter(filter);
    };

    return (
        <PaloThemeProvider>
            <Select
                variant="standard"
                disableUnderline
                value={value}
                onChange={event => onChange(String(event.target.value))}
                sx={{ fontSize: '12.5px', color: tokens.color.ink2, '& .MuiSelect-select': { py: 0.5 } }}
            >
                <MenuItem value={ALL}>
                    <FormattedMessage id="app.filter.showAll" />
                </MenuItem>
                <MenuItem value={String(userContact.id)}>
                    <FormattedMessage id="app.filter.showOwn" />
                </MenuItem>
            </Select>
        </PaloThemeProvider>
    );
};
